import moment from 'moment';
import React from 'react';
import { CheckboxOld } from 'shared/components';
import useUI from 'shared/hooks/use-ui';
import { TIME_FORMAT } from 'src/utils/time-utils';
import { ActiveSwitch } from '../active-switch';
import { MoreButton, Option } from '../table-more-button';
import { Day } from './utils';

import {
  DayName,
  DayRowContainer,
  HoursContainer,
  HoursSelectRow,
  MoreButtonContainer,
  StyledSelect,
  ActiveSwitchWrapper,
  TwentyFourHours,
} from './styles';

function bothTimesAreMidnight(startTime: string, endTime: string): boolean {
  return startTime === moment().startOf('day').format('HH:mm') && endTime === moment().endOf('day').format('HH:mm');
}

type DayRowProps = {
  day: Day;
  times: string[];
  dispensaryTimezone: string;
  handleToggleAllDay: (allDay: boolean, day: Day) => void;
  handleUpdateDay: (updatedDay: Day) => void;
  handleCopyScheduleToAll: (day: Day) => void;
};

export function DayRow({
  day,
  times,
  dispensaryTimezone,
  handleCopyScheduleToAll,
  handleToggleAllDay,
  handleUpdateDay,
}: DayRowProps): JSX.Element {
  const UI = useUI();

  function handleUpdateStartTimeForDay(startTime: string): void {
    if (moment(startTime, TIME_FORMAT).isAfter(moment(day.endTime, TIME_FORMAT))) {
      handleUpdateDay({
        ...day,
        startTime,
        endTime: moment(startTime, TIME_FORMAT).add(15, 'minutes').format(TIME_FORMAT),
      });
    } else if (bothTimesAreMidnight(startTime, day.endTime)) {
      UI.showErnie("A day's start and end time cannot be midnight. Use the All Day checkbox instead.", 'danger');
    } else {
      handleUpdateDay({ ...day, startTime });
    }
  }

  function handleUpdateEndTimeForDay(endTime: string): void {
    if (moment(endTime, TIME_FORMAT).isBefore(moment(day.startTime, TIME_FORMAT))) {
      endTime = moment(day.startTime, TIME_FORMAT).add(15, 'minutes').format(TIME_FORMAT);
      UI.showErnie("A day's end time must be after its start time.", 'danger');
    } else if (bothTimesAreMidnight(day.startTime, day.endTime)) {
      UI.showErnie("A day's start and end time cannot be midnight. Use the All Day checkbox instead.", 'danger');
      return;
    }

    handleUpdateDay({ ...day, endTime });
  }

  return (
    <DayRowContainer key={day.key}>
      <ActiveSwitchWrapper>
        <ActiveSwitch
          key={`enabled_${day.key}`}
          inactiveStateColor='#E5E5E5'
          active={day.enabled}
          usePropForValue
          onClick={() => {
            handleUpdateDay({ ...day, enabled: !day.enabled });
          }}
        />
      </ActiveSwitchWrapper>

      <DayName>{day.dayName}</DayName>
      {day.enabled && (
        <>
          <CheckboxOld
            key={`allDay_${day.key}`}
            checked={day.allDay}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleToggleAllDay(e.target.checked, day);
            }}
          />
          <span>All Day</span>

          <HoursContainer>
            {day.allDay ? (
              <TwentyFourHours>24 hours</TwentyFourHours>
            ) : (
              <HoursSelectRow>
                <StyledSelect
                  timezoneLeftPosition='68px'
                  width='130px'
                  height={32}
                  fontSize='13px'
                  value={moment(day.startTime, TIME_FORMAT).format(TIME_FORMAT)}
                  name={`startTime_${day.key}`}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateStartTimeForDay(e.target.value)}
                  timezone={`(${moment.tz(dispensaryTimezone).zoneAbbr()})`}
                >
                  {times.map((time: string) => (
                    <option key={time} value={time}>
                      {moment(time, TIME_FORMAT).format('h:mm A')}
                    </option>
                  ))}
                </StyledSelect>
                <span> - </span>
                <StyledSelect
                  timezoneLeftPosition='68px'
                  width='130px'
                  height={32}
                  fontSize='13px'
                  value={moment(day.endTime, TIME_FORMAT).format(TIME_FORMAT)}
                  name={`endTime_${day.key}`}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateEndTimeForDay(e.target.value)}
                  timezone={`(${moment.tz(dispensaryTimezone).zoneAbbr()})`}
                >
                  {times.map((time: string) => (
                    <option key={time} value={time}>
                      {moment(time, TIME_FORMAT).format('h:mm A')}
                    </option>
                  ))}
                </StyledSelect>
              </HoursSelectRow>
            )}
          </HoursContainer>

          <MoreButtonContainer>
            <MoreButton>
              <Option
                onClick={() => {
                  handleCopyScheduleToAll(day);
                }}
              >
                Copy schedule to all
              </Option>
            </MoreButton>
          </MoreButtonContainer>
        </>
      )}
    </DayRowContainer>
  );
}
